import { writable, derived } from 'svelte/store';
import { getFromStorage, saveToStorage } from '../utils/localStorage';
import { calculateCost } from '../utils/calculator';

export type Currency = 'EUR' | 'USD' | 'GBP' | 'JPY' | 'CHF' | 'CAD' | 'AUD';

interface MeetingState {
  attendees: number;
  hourlyRate: number;
  elapsedSeconds: number;
  isRunning: boolean;
  currency: Currency;
}

const DEFAULT_STATE: MeetingState = {
  attendees: 5,
  hourlyRate: 50,
  elapsedSeconds: 0,
  isRunning: false,
  currency: 'EUR',
};

/**
 * Initialize state from localStorage
 */
function initState(): MeetingState {
  const attendees = getFromStorage<number>('attendees', DEFAULT_STATE.attendees);
  const currency = getFromStorage<Currency>('currency', DEFAULT_STATE.currency);
  
  return {
    ...DEFAULT_STATE,
    attendees,
    currency,
  };
}

/**
 * Meeting store with localStorage persistence
 */
function createMeetingStore() {
  const { subscribe, set, update } = writable<MeetingState>(initState());
  
  let intervalId: number | null = null;

  return {
    subscribe,
    
    startTimer: () => {
      update((state) => {
        if (state.isRunning) return state;
        
        const setIntervalFn = typeof window !== 'undefined' ? window.setInterval : setInterval;
        intervalId = setIntervalFn(() => {
          update((s) => ({
            ...s,
            elapsedSeconds: s.elapsedSeconds + 1,
          }));
        }, 1000) as unknown as number;
        
        return { ...state, isRunning: true };
      });
    },
    
    stopTimer: () => {
      if (intervalId !== null) {
        const clearIntervalFn = typeof window !== 'undefined' ? window.clearInterval : clearInterval;
        clearIntervalFn(intervalId);
        intervalId = null;
      }
      update((state) => ({ ...state, isRunning: false }));
    },
    
    resetTimer: () => {
      if (intervalId !== null) {
        const clearIntervalFn = typeof window !== 'undefined' ? window.clearInterval : clearInterval;
        clearIntervalFn(intervalId);
        intervalId = null;
      }
      update((state) => ({
        ...state,
        elapsedSeconds: 0,
        isRunning: false,
      }));
    },
    
    updateAttendees: (attendees: number) => {
      const validAttendees = Math.max(1, Math.floor(attendees));
      update((state) => {
        const newState = { ...state, attendees: validAttendees };
        saveToStorage('attendees', validAttendees);
        return newState;
      });
    },
    
    updateHourlyRate: (rate: number) => {
      const validRate = Math.max(0, rate);
      update((state) => ({ ...state, hourlyRate: validRate }));
    },
    
    updateCurrency: (currency: Currency) => {
      update((state) => {
        const newState = { ...state, currency };
        saveToStorage('currency', currency);
        return newState;
      });
    },
    
    reset: () => {
      if (intervalId !== null) {
        const clearIntervalFn = typeof window !== 'undefined' ? window.clearInterval : clearInterval;
        clearIntervalFn(intervalId);
        intervalId = null;
      }
      set(initState());
      saveToStorage('attendees', DEFAULT_STATE.attendees);
      saveToStorage('currency', DEFAULT_STATE.currency);
    },
  };
}

export const meetingStore = createMeetingStore();

/**
 * Derived store for calculated cost
 */
export const costStore = derived(meetingStore, ($meeting) => {
  return calculateCost(
    $meeting.attendees,
    $meeting.hourlyRate,
    $meeting.elapsedSeconds,
    $meeting.currency
  );
});
